import React, { useState } from 'react';
import { Gift, ChevronLeft, ChevronRight, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { WheelData } from '../../../../types';

interface ProductsShowcaseProps {
  wheelData: WheelData;
  theme: {
    background_color: string;
    text_color: string;
    font_family: string;
    logo_url?: string;
  };
}

const ProductsShowcase: React.FC<ProductsShowcaseProps> = ({ wheelData, theme }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const products = wheelData.products.filter(p => !p.is_no_prize && p.in_stock);

  if (products.length === 0) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % products.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  const currentProduct = products[currentIndex];

  return (
    <div className="mb-6">
      {/* Versão compacta - sempre visível */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full bg-white rounded-lg hover:shadow-lg transition-all p-4 mb-2 ${
          !isExpanded ? 'animate-pulse-soft' : 'shadow-md'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-full"
              style={{ backgroundColor: `${theme.background_color}15` }}
            >
              <Gift 
                className="h-5 w-5" 
                style={{ color: theme.background_color }} 
              />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-bold text-gray-900">
                {products.length} {products.length === 1 ? 'Prêmio Disponível' : 'Prêmios Disponíveis'}
              </h3>
              <p className="text-xs text-gray-600">
                {isExpanded ? 'Ocultar prêmios' : 'Ver todos os prêmios'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mini preview dos produtos */}
            {!isExpanded && (
              <div className="flex -space-x-2">
                {products.slice(0, 3).map((product) => (
                  <div
                    key={product.id}
                    className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-100"
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                ))}
                {products.length > 3 && (
                  <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600">
                      +{products.length - 3}
                    </span>
                  </div>
                )}
              </div>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
      </button>

      {/* Versão expandida - carrossel completo */}
      {isExpanded && (
        <div className="overflow-hidden">
          <div className="relative bg-white rounded-lg shadow-lg overflow-hidden mb-4">
            {/* Produto atual */}
            <div className="p-6">
              <div className="relative mb-4">
                {currentProduct.image_url ? (
                  <div className="aspect-square w-full max-w-xs mx-auto rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={currentProduct.image_url}
                      alt={currentProduct.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full flex items-center justify-center';
                          fallback.innerHTML = `<div class="text-gray-400"><svg class="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path></svg></div>`;
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="aspect-square w-full max-w-xs mx-auto rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <Package className="h-16 w-16 text-gray-400" />
                  </div>
                )}
              </div>

              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {currentProduct.name}
                </h3>
                {currentProduct.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {currentProduct.description}
                  </p>
                )}
                <div
                  className="inline-block px-3 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor: `${theme.background_color}15`,
                    color: theme.background_color
                  }}
                >
                  {currentProduct.probability_display} de chance
                </div>
              </div>
            </div>

            {/* Controles de navegação - apenas se houver mais de um produto */}
            {products.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
                  aria-label="Produto anterior"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
                  aria-label="Próximo produto"
                >
                  <ChevronRight className="h-5 w-5 text-gray-700" />
                </button>

                {/* Indicadores */}
                <div className="flex justify-center gap-2 pb-4">
                  {products.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrentIndex(index)}
                      className="w-2 h-2 rounded-full transition-all"
                      style={{
                        backgroundColor: index === currentIndex ? theme.background_color : '#D1D5DB',
                        opacity: index === currentIndex ? 1 : 0.5
                      }}
                      aria-label={`Ver produto ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Mini galeria - mostra todos os produtos em versão miniatura */}
          {products.length > 1 && (
            <div className="overflow-x-auto">
              <div className="flex gap-2 pb-2">
                {products.map((product, index) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                      index === currentIndex
                        ? 'scale-105 ring-2'
                        : 'opacity-60 hover:opacity-100 border-2 border-transparent'
                    }`}
                    style={{
                      borderColor: index === currentIndex ? theme.background_color : undefined,
                      '--tw-ring-color': theme.background_color
                    } as React.CSSProperties}
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full flex items-center justify-center bg-gray-100';
                            fallback.innerHTML = `<svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path></svg>`;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Package className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsShowcase;
